import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useOrg } from '../data/orgs';
import { useVisibleDeals } from '../data/permissions';
import { useToast } from '../components/Toast';
import Modal from '../components/Modal';
import Icon from '../components/Icons';
import { useDoc } from '../data/firestore';
import { grantIntake, grantCreate, isAiConfigured } from '../data/legalApi';
import { GRANT_STAGES, DeadlineChip, fmtAmount, daysLeft } from '../components/GrantApplicationTab';

// Grants desk — the firm's tracker across every grant application project
// (projectType 'grant'): one row per opportunity, grouped by client company,
// with stage, amount, deadline, matrix and review state. "New grant" makes
// one project; "Import pipeline" pastes a client's description of what they
// are chasing, previews the recognised rows, then creates a project each.
// The Company Profile hub per company is created on first use by the server.

const STAGE_LABEL = Object.fromEntries(GRANT_STAGES);
const SOURCES = ['federal', 'state', 'local', 'foundation'];
const VEHICLES = ['SBIR', 'STTR', 'grant', 'incentive', 'cooperative', 'contract'];
const slugOf = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40);
const sumMax = (rows) => rows.reduce((n, r) => n + (Number(r.grant?.amountMax ?? r.amountMax) || 0), 0);
const fmtTotal = (n) => (n >= 1e6 ? `$${(n / 1e6).toFixed(1)}M` : n > 0 ? `$${Math.round(n / 1e3)}K` : '$0');

const TABS = [
  { key: 'active', label: 'Active' },
  { key: 'due', label: 'Due ≤ 60d' },
  { key: 'review', label: 'In review' },
  { key: 'submitted', label: 'Submitted' },
  { key: 'all', label: 'All' },
];

function CompanyField({ value, onChange, companies }) {
  return (
    <div className="cs-field">
      <label htmlFor="g-company">Client company</label>
      <input id="g-company" className="cs-input" list="g-companies" value={value} onChange={(e) => onChange(e.target.value)} placeholder="Creative 3D Technologies" autoFocus />
      <datalist id="g-companies">{companies.map((c) => <option key={c.tag} value={c.name} />)}</datalist>
      <div className="cs-help">Becomes the tag <code>{slugOf(value) || 'company'}</code> shared by the company's Grants program hub (Company Profile) and every application.</div>
    </div>
  );
}

function NewGrantModal({ orgId, companies, onClose }) {
  const toast = useToast();
  const navigate = useNavigate();
  const [company, setCompany] = useState(companies[0]?.name || '');
  const [f, setF] = useState({ program: '', funder: '', source: 'federal', vehicle: 'grant', phase: '', amountMin: '', amountMax: '', matchRequired: false, deadline: '', notes: '', pastedText: '' });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((o) => ({ ...o, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!slugOf(company) || !f.program.trim()) return;
    setBusy(true);
    try {
      const r = await grantCreate({
        orgId, company: slugOf(company), companyName: company.trim(), program: f.program.trim(), funder: f.funder.trim(), source: f.source, vehicle: f.vehicle, phase: f.phase.trim(),
        amountMin: f.amountMin ? Number(f.amountMin) : undefined, amountMax: f.amountMax ? Number(f.amountMax) : undefined, matchRequired: f.matchRequired,
        deadline: f.deadline || undefined, notes: f.notes.trim() || undefined, pastedText: f.pastedText.trim() || undefined,
      });
      toast(`Project created${r.filed?.length ? ' — solicitation ingesting' : ''}`, 'ok');
      onClose();
      navigate(`/deals/${r.dealId}?tab=application`);
    } catch (err) { toast(err.message, 'danger'); } finally { setBusy(false); }
  };
  return (
    <Modal title="New grant application" onClose={onClose} maxWidth={640}>
      <form className="u-stack" style={{ '--gap': 'var(--sp-3)' }} onSubmit={submit}>
        <CompanyField value={company} onChange={setCompany} companies={companies} />
        <div className="cs-field"><label htmlFor="g-program">Program / solicitation</label><input id="g-program" className="cs-input" value={f.program} onChange={set('program')} placeholder="NSF SBIR Phase I — Advanced Manufacturing" /></div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-3)' }}>
          <div className="cs-field"><label htmlFor="g-funder">Funder</label><input id="g-funder" className="cs-input" value={f.funder} onChange={set('funder')} placeholder="National Science Foundation" /></div>
          <div className="cs-field"><label htmlFor="g-phase">Phase / round</label><input id="g-phase" className="cs-input" value={f.phase} onChange={set('phase')} placeholder="Phase I" /></div>
          <div className="cs-field"><label htmlFor="g-source">Source</label><select id="g-source" className="cs-input" value={f.source} onChange={set('source')}>{SOURCES.map((s) => <option key={s}>{s}</option>)}</select></div>
          <div className="cs-field"><label htmlFor="g-vehicle">Vehicle</label><select id="g-vehicle" className="cs-input" value={f.vehicle} onChange={set('vehicle')}>{VEHICLES.map((s) => <option key={s}>{s}</option>)}</select></div>
          <div className="cs-field"><label htmlFor="g-min">Amount min ($)</label><input id="g-min" className="cs-input" type="number" min="0" value={f.amountMin} onChange={set('amountMin')} /></div>
          <div className="cs-field"><label htmlFor="g-max">Amount max ($)</label><input id="g-max" className="cs-input" type="number" min="0" value={f.amountMax} onChange={set('amountMax')} placeholder="380000" /></div>
          <div className="cs-field"><label htmlFor="g-deadline">Deadline</label><input id="g-deadline" className="cs-input" type="date" value={f.deadline} onChange={set('deadline')} /></div>
          <label className="cs-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 'var(--sp-2)', marginTop: 'var(--sp-5)' }}><input type="checkbox" checked={f.matchRequired} onChange={set('matchRequired')} /> Match / cost share required</label>
        </div>
        <div className="cs-field"><label htmlFor="g-notes">Notes</label><input id="g-notes" className="cs-input" value={f.notes} onChange={set('notes')} placeholder="Pre-approved; STRATFI follow-on possible" /></div>
        <div className="cs-field">
          <label htmlFor="g-text">Solicitation text (optional)</label>
          <textarea id="g-text" className="cs-input" rows={5} value={f.pastedText} onChange={set('pastedText')} placeholder="Paste the funding announcement here, or upload the PDF on the project's Documents tab afterwards." />
        </div>
        <div style={{ display: 'flex', gap: 'var(--sp-2)', justifyContent: 'flex-end' }}>
          <button type="button" className="cs-btn cs-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="cs-btn cs-btn-primary" disabled={busy || !slugOf(company) || !f.program.trim()}>{busy ? 'Creating…' : 'Create project'}</button>
        </div>
      </form>
    </Modal>
  );
}

function ImportModal({ orgId, companies, onClose }) {
  const toast = useToast();
  const [company, setCompany] = useState(companies[0]?.name || '');
  const [text, setText] = useState('');
  const [rows, setRows] = useState(null);
  const [picked, setPicked] = useState({});
  const [busy, setBusy] = useState('');
  // The parse is a queued run on the company's hub project (slow models
  // outlive the gateway); we watch the run doc and lift its rows when done.
  const [runPath, setRunPath] = useState(null);
  const run = useDoc(runPath);
  useEffect(() => {
    if (!run || !runPath) return;
    if (run.status === 'done') {
      const got = run.result?.rows || [];
      setRows(got); setPicked(Object.fromEntries(got.map((_, i) => [i, true]))); setRunPath(null); setBusy('');
    } else if (run.status === 'error') { toast(run.error || 'the parse failed', 'danger'); setRunPath(null); setBusy(''); }
  }, [run, runPath, toast]);
  const preview = async () => {
    setBusy('preview');
    try {
      const r = await grantIntake({ orgId, company: slugOf(company), companyName: company.trim(), text, dryRun: true });
      if (r.runPath) setRunPath(r.runPath); else { setRows(r.rows || []); setBusy(''); }
    } catch (err) { toast(err.message, 'danger'); setBusy(''); }
  };
  const create = async () => {
    const chosen = (rows || []).filter((_, i) => picked[i]);
    if (!chosen.length) return;
    setBusy('create');
    try {
      const r = await grantIntake({ orgId, company: slugOf(company), companyName: company.trim(), rows: chosen });
      toast(`${r.created.length} project${r.created.length === 1 ? '' : 's'} created${r.skipped.length ? `, ${r.skipped.length} already existed` : ''}`, 'ok');
      onClose();
    } catch (err) { toast(err.message, 'danger'); } finally { setBusy(''); }
  };
  const edit = (i, k, v) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <Modal title="Import a grants pipeline" onClose={onClose} maxWidth={820}>
      <div className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
        <CompanyField value={company} onChange={setCompany} companies={companies} />
        {!rows && (
          <>
            <div className="cs-field">
              <label htmlFor="g-pipe">What the client is pursuing</label>
              <textarea id="g-pipe" className="cs-input" rows={10} value={text} onChange={(e) => setText(e.target.value)} placeholder="Paste the client's email or memo — programs, agencies, amounts, phases, status. One project is created per opportunity." />
            </div>
            <div style={{ display: 'flex', gap: 'var(--sp-2)', justifyContent: 'flex-end' }}>
              <button type="button" className="cs-btn cs-btn-ghost" onClick={onClose}>Cancel</button>
              <button type="button" className="cs-btn cs-btn-primary" disabled={busy === 'preview' || !slugOf(company) || text.trim().length < 20 || !isAiConfigured()} onClick={preview}>{busy === 'preview' ? 'Reading… (up to 2 min)' : 'Preview opportunities'}</button>
            </div>
          </>
        )}
        {rows && (
          <>
            <div className="cs-help">{rows.length} opportunit{rows.length === 1 ? 'y' : 'ies'} recognised — untick any you don't want, fix names or amounts inline, then create.</div>
            <div style={{ display: 'grid', gap: 'var(--sp-1)', maxHeight: 420, overflow: 'auto' }}>
              {rows.map((r, i) => (
                <div key={i} className="cs-card" style={{ padding: 'var(--sp-2) var(--sp-3)', display: 'grid', gridTemplateColumns: 'auto 1fr 1fr 110px 110px 120px', gap: 'var(--sp-2)', alignItems: 'center', opacity: picked[i] ? 1 : 0.5 }}>
                  <input type="checkbox" checked={!!picked[i]} onChange={(e) => setPicked((p) => ({ ...p, [i]: e.target.checked }))} aria-label="Include" />
                  <input className="cs-input" value={r.program} onChange={(e) => edit(i, 'program', e.target.value)} aria-label="Program" />
                  <input className="cs-input" value={r.funder} onChange={(e) => edit(i, 'funder', e.target.value)} aria-label="Funder" placeholder="Funder" />
                  <input className="cs-input" type="number" value={r.amountMax ?? ''} onChange={(e) => edit(i, 'amountMax', e.target.value ? Number(e.target.value) : null)} aria-label="Amount max" placeholder="max $" />
                  <select className="cs-input" value={r.stage} onChange={(e) => edit(i, 'stage', e.target.value)} aria-label="Stage">{GRANT_STAGES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
                  <span className="u-xs u-muted">{[r.source, r.vehicle, r.phase].filter(Boolean).join(' · ')}</span>
                  {r.notes && <div className="u-xs u-muted" style={{ gridColumn: '2 / -1' }}>{r.notes}</div>}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 'var(--sp-2)', justifyContent: 'flex-end' }}>
              <button type="button" className="cs-btn cs-btn-ghost" onClick={() => setRows(null)}>Back</button>
              <button type="button" className="cs-btn cs-btn-primary" disabled={busy === 'create' || !rows.some((_, i) => picked[i])} onClick={create}>{busy === 'create' ? 'Creating…' : `Create ${rows.filter((_, i) => picked[i]).length} project${rows.filter((_, i) => picked[i]).length === 1 ? '' : 's'}`}</button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

export default function GrantsDesk() {
  const { orgId } = useOrg();
  const { deals: allDeals } = useVisibleDeals();
  const [tab, setTab] = useState('active');
  const [company, setCompany] = useState('');
  const [q, setQ] = useState('');
  const [modal, setModal] = useState('');
  const deals = useMemo(() => (allDeals ? allDeals.filter((d) => d.projectType === 'grant') : null), [allDeals]);

  const hubs = useMemo(() => (deals || []).filter((d) => d.grant?.hub), [deals]);
  const apps = useMemo(() => (deals || []).filter((d) => !d.grant?.hub), [deals]);
  const companies = useMemo(() => {
    const m = new Map();
    for (const h of hubs) m.set(h.grant.company, { tag: h.grant.company, name: h.grant.companyName || h.grant.company, hubId: h.id, profileDocId: h.coreDocId });
    for (const a of apps) if (a.grant?.company && !m.has(a.grant.company)) m.set(a.grant.company, { tag: a.grant.company, name: a.grant.companyName || a.grant.company });
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [hubs, apps]);

  const rows = useMemo(() => {
    let list = apps.filter((d) => !company || d.grant?.company === company);
    const st = (d) => d.grant?.stage || 'prospect';
    if (tab === 'active') list = list.filter((d) => !['submitted', 'awarded', 'declined'].includes(st(d)));
    else if (tab === 'due') list = list.filter((d) => { const n = daysLeft(d.grant?.deadline); return n !== null && n >= 0 && n <= 60; });
    else if (tab === 'review') list = list.filter((d) => st(d) === 'internal-review');
    else if (tab === 'submitted') list = list.filter((d) => ['submitted', 'awarded', 'declined'].includes(st(d)));
    if (q.trim()) { const n = q.trim().toLowerCase(); list = list.filter((d) => `${d.name} ${d.grant?.program} ${d.grant?.funder} ${d.grant?.vehicle}`.toLowerCase().includes(n)); }
    return list.sort((a, b) => (a.grant?.deadline || 9e15) - (b.grant?.deadline || 9e15) || (b.grant?.amountMax || 0) - (a.grant?.amountMax || 0));
  }, [apps, tab, company, q]);

  const stats = useMemo(() => {
    const scoped = apps.filter((d) => !company || d.grant?.company === company);
    const st = (d) => d.grant?.stage || 'prospect';
    return {
      total: scoped.length, pipeline: sumMax(scoped.filter((d) => !['awarded', 'declined'].includes(st(d)))),
      awarded: sumMax(scoped.filter((d) => st(d) === 'awarded')), due60: scoped.filter((d) => { const n = daysLeft(d.grant?.deadline); return n !== null && n >= 0 && n <= 60; }).length,
      drafting: scoped.filter((d) => ['drafting', 'internal-review'].includes(st(d))).length,
    };
  }, [apps, company]);

  if (!orgId) return <div className="cs-page"><p className="cs-help">Pick an organization first.</p></div>;

  return (
    <div className="cs-page" data-screen="grants-desk">
      <div className="cs-page-head">
        <div>
          <h1>Grants</h1>
          <p className="cs-help">Every grant, SBIR/STTR and incentive application the firm is running for its clients — one project per opportunity, drafted from each company's profile and checked against the solicitation.</p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
          <button type="button" className="cs-btn cs-btn-outline" onClick={() => setModal('import')}><Icon name="upload" size={14} /> Import pipeline</button>
          <button type="button" className="cs-btn cs-btn-primary" onClick={() => setModal('new')}><Icon name="plus" size={14} /> New grant</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap', marginBottom: 'var(--sp-4)' }}>
        <span className="cs-chip">{stats.total} application{stats.total === 1 ? '' : 's'}</span>
        <span className="cs-chip" style={{ color: 'var(--info)' }}>{fmtTotal(stats.pipeline)} in pipeline</span>
        {stats.awarded > 0 && <span className="cs-chip" style={{ color: 'var(--ok)' }}>{fmtTotal(stats.awarded)} awarded</span>}
        <span className="cs-chip">{stats.due60} due ≤60d</span>
        <span className="cs-chip">{stats.drafting} drafting / in review</span>
      </div>

      <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap', alignItems: 'center', marginBottom: 'var(--sp-4)' }}>
        {TABS.map((t) => <button key={t.key} type="button" className={`cs-btn cs-btn-sm ${tab === t.key ? 'cs-btn-primary' : 'cs-btn-ghost'}`} onClick={() => setTab(t.key)}>{t.label}</button>)}
        {companies.length > 1 && (
          <select className="cs-input" value={company} onChange={(e) => setCompany(e.target.value)} aria-label="Company" style={{ maxWidth: 220 }}>
            <option value="">All companies</option>
            {companies.map((c) => <option key={c.tag} value={c.tag}>{c.name}</option>)}
          </select>
        )}
        <input className="cs-input" style={{ marginLeft: 'auto', maxWidth: 260 }} placeholder="Filter program / funder…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      {companies.length > 0 && (
        <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap', marginBottom: 'var(--sp-4)' }}>
          {companies.filter((c) => !company || c.tag === company).map((c) => (
            <Link key={c.tag} className="cs-chip" to={c.profileDocId ? `/deals/${c.hubId}/docs/${c.profileDocId}` : c.hubId ? `/deals/${c.hubId}` : '/grants'} title="Company Profile — the fact base every narrative cites">
              {c.name} · profile ↗
            </Link>
          ))}
        </div>
      )}

      {deals === null && <div className="cs-skeleton" style={{ height: 120 }} />}
      {deals !== null && !rows.length && (
        <div className="cs-empty">{apps.length === 0 ? 'No grant applications yet — import a client\'s pipeline or start one with New grant.' : 'Nothing matches this view.'}</div>
      )}

      <div style={{ display: 'grid', gap: 'var(--sp-2)' }}>
        {rows.map((d) => {
          const g = d.grant || {};
          const stage = g.stage || 'prospect';
          const rev = d.grantReview;
          const crit = (d.grantChecks?.findings || []).filter((f) => f.severity === 'critical').length;
          return (
            <Link key={d.id} to={`/deals/${d.id}?tab=application`} className="cs-card" style={{ padding: 'var(--sp-3) var(--sp-4)', display: 'flex', gap: 'var(--sp-3)', alignItems: 'center', flexWrap: 'wrap', textDecoration: 'none', color: 'inherit' }}>
              <div style={{ flex: '1 1 320px', minWidth: 240 }}>
                <div style={{ fontWeight: 600 }}>{g.program || d.name}{g.phase ? <span className="u-muted"> · {g.phase}</span> : null}</div>
                <div className="u-xs u-muted">{[g.companyName || g.company, g.funder, g.source, g.vehicle].filter(Boolean).join(' · ')}</div>
              </div>
              <span className="cs-chip" title="Award range">{fmtAmount(g)}</span>
              {g.matchRequired && <span className="cs-chip" style={{ color: '#ffd166' }}>match</span>}
              <DeadlineChip ms={g.deadline} />
              <span className="cs-chip" style={{ color: stage === 'awarded' ? 'var(--ok)' : stage === 'declined' ? 'var(--muted)' : stage === 'internal-review' ? 'var(--info)' : undefined }}>{STAGE_LABEL[stage] || stage}</span>
              {d.openPointCount > 0 && <span className="cs-chip">{d.openPointCount} open</span>}
              {rev && <span className="cs-chip" style={{ color: rev.score >= 4 ? 'var(--ok)' : rev.score >= 3 ? '#ffd166' : 'var(--accent, #ff6b6b)' }} title={`Mock panel ${rev.fundable}`}>panel {rev.score}/5</span>}
              {crit > 0 && <span className="cs-chip" style={{ color: 'var(--accent, #ff6b6b)' }}>{crit} critical</span>}
            </Link>
          );
        })}
      </div>

      {modal === 'new' && <NewGrantModal orgId={orgId} companies={companies} onClose={() => setModal('')} />}
      {modal === 'import' && <ImportModal orgId={orgId} companies={companies} onClose={() => setModal('')} />}
    </div>
  );
}
